
/*
 * VNCtalk - an enterprise real-time communication solution including chat, video and audio conferencing, screen sharing, voice messaging, file sharing, broadcasts, document collaboration and much more.
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import { Message, MessageStatus } from "../models/message.model";
import { JID } from "../models/jid.model";
import { SearchMessage } from "../models/search-message.model";
import { environment } from "../../environments/environment";
import { ElectronService } from "app/shared/providers/electron.service";
import { CommonUtil } from "./common.util";
import { DateUtil } from "../utils/date.util";
import { ConversationRepository } from "../repositories/conversation.repository";
import { filter, take } from "rxjs";
import { Store } from "@ngrx/store";
import { TalkRootState } from "../reducers";
import { FilesStorageService } from "../services/files-storage.service";
import formatDistance from "date-fns/formatDistance";

const ALLOWED_PREVIEW_EXT = ["html", "htm", "php", "asp", "aspx", "jsp"];

//
const VS_ORIGINAL_MESSAGE_ADDITION = 12;
const VS_REACTION_HEIGHT = 27;
//
const VS_DELETED_MESSAGE_HEIGHT = 43;
//
const VS_LINK_PREVIEW_HEIGHT = 183 + 20; // 183 is height, 20 are paddings
const VS_REDMINE_PREVIEW_HEIGHT = 300;
const VS_REDMINE_VERSION_PREVIEW_HEIGHT = 245;

//
const VS_CHANNEL_TOPIC = 273;
const VS_CHANNEL_CHANNEL = 182;
const VS_CHANNEL_COMMENT = 243;
const VS_CHANNEL_SOCIAL = 400; // TODO
//

// TODO: calls system mesasge may have dinamic height
const VS_MESSAGE_GROUP_ACTION = 24 + 32 + 2; // 24 line height + 32 padding + 2 border;
const VS_MESSAGE_GROUP_ACTION_2_LINES = 24 + 20 + 32 + 2; // 24 line height + 20 second line height + 32 padding + 2 border;
const VS_MESSAGE_CALL_SIGNAL = 105;
const VS_MESSAGE_CALL_SIGNAL_2_LINES = 89 + 5 + 5 + 1 + 1;
const VS_MESSAGE_CALL_SIGNAL_3_LINES = 113 + 5 + 5 + 1 + 1;


enum ChannelEntityType {
  CHANNEL = "channels",
  TOPIC = "topics",
  COMMENT = "commentId",
  SOCIAL_TOPIC = "socialTopic",
  SOCIAL = "social"
}

export class MessageUtil {
  static HEIGHT_CACHE = new Map<string, any>();
  static CATCH_MESSAGES_SIZE = {};
  static noAnswer: any;
  static callEnded: any;
  // VS pieces
  //
  static measureTextHeights(
    clientWidth: number,
    messages: Message[], isGroupChat: boolean,
    jitsiURL: string, redmineUrl: string,
    callHistory: any, unreadids: string[],
    userJID: any,
    fontSize: string,
    repos: any,
    ) {

    // const fontSize = Math.ceil(14 * 1.14);  // 14   .font-size-14 { font-size: 14px;}   // 1.14rem
    const fontSizeNumber = +fontSize.split("-")[1];
    const lineHeight = 1.25;
    // console.log("[measureTextHeights][fontSizeNumber]", fontSizeNumber);
    const isHinTheme = localStorage.getItem("theme") === "hin";

    const mapped = [];
    messages.forEach((m, idx) => {
      mapped.push(this.measureMessageTextHeight(m, idx, clientWidth, fontSizeNumber, lineHeight, jitsiURL, redmineUrl, messages, isGroupChat, callHistory, unreadids, isHinTheme, userJID, repos));
    });

    // const mapped = messages.map(m => this.measureMessageTextHeight(m, idx, clientWidth, fontSizeNumber, lineHeight, jitsiURL, redmineUrl));

    const sum = mapped.reduce((partialSum, a) => partialSum + a, 0);
    return [mapped, sum];
  }
  static updateMessagesFlag(messages, unreadids) {
    messages.forEach((m, idx) => {
      this.updateMessageFlag(m, idx, messages, unreadids);
    });
  }

  static updateMessageFlag(
    message: Message, idx: number, messages: Message[], unreadids: string[]) {
      message.isDifferentDayThanLastMessage = this.isDifferentDayThanLastMessage(idx, messages);
      message.isDifferentSenderThanLastMessage = this.isDifferentSenderThanLastMessage(idx, messages);
      message.highlighted = this.isHighlighted(idx, message, unreadids);
  }

  static measureMessageTextHeight(
          message: Message, idx: number,
          clientWidth: number, fontSize: number, lineHeight: number,
          jitsiURL: string, redmineUrl: string,
          messages: Message[], isGroupChat: boolean, callHistory: any, unreadids: string[],
          isHinTheme: boolean,
          userJID: any, repos: any): number {
    const cachedData = this.getMessageTextHeightFromCache(message.id);
    if (cachedData) {
      const {
        testHeight
      } = cachedData;
      message.isDifferentDayThanLastMessage = this.isDifferentDayThanLastMessage(idx, messages);
      message.isDifferentSenderThanLastMessage = this.isDifferentSenderThanLastMessage(idx, messages);
      message.highlighted = this.isHighlighted(idx, message, unreadids);
      return testHeight;
    }

    message.highlighted = this.isHighlighted(idx, message, unreadids);

    // [x] attachments
    // [x] texts
    // [x] reply
    // [x] forward
    // [x] delete
    // [x] link preview
    // [x] redmine preview
    // [x] system messages
    // [+-] call notifications

    let isUrlOrRedminePreview = false;

    let msgHeight = 0;

    message.isDifferentSenderThanLastMessage = this.isDifferentSenderThanLastMessage(idx, messages);

    if (message.isDifferentSenderThanLastMessage) {
      msgHeight = msgHeight + 72;  // vp-chat-window .chat-message.is-first-message .vnctalk-message:not(.private-chat) {margin-bottom: 10px;}
    }


    // deleted
    if (message.isDeleted) {
      msgHeight = VS_DELETED_MESSAGE_HEIGHT;

    // system messages
    } else if (message.group_action) {
      if (message.group_action.type === "ADD_PARTICIPANTS") {
        msgHeight = VS_MESSAGE_GROUP_ACTION_2_LINES;
      } else {
        msgHeight = VS_MESSAGE_GROUP_ACTION;
      }

    // call history
    } else if (message.vncTalkConference) {
      const ch = callHistory[message.id];
      if (ch?.length >= 2) {
        if (ch.find(c => c.body === "USER_HAS_NOT_ANSWERED")) {
          msgHeight = this.measureCallMessage(message, clientWidth, isHinTheme, userJID, repos); // for calculation of call history height.
        } else {
          msgHeight = this.measureCallMessage(message, clientWidth, isHinTheme, userJID, repos); // for calculation of call history height.
        }
      } else {
        msgHeight = this.measureCallMessage(message, clientWidth, isHinTheme, userJID, repos); // for calculation of call history height.
      }

    } else {
      // attachments
      let testHeight = 0;
      let isMessageReply: boolean = false;     // check if message is reply message
      if ((message && message.originalMessage && message.originalMessage.replyMessage)) {
        isMessageReply = true;
      }
      let isBodyHaveImage = this.findAttachmentIsImageOrVideo(message.body, true); // check message.body have image or video.
      if (message.attachment && message.attachment.fileName
        && message.attachment.url && message.attachment.url.startsWith("http") && !isMessageReply) {
        const isImage = CommonUtil.isImage(message.attachment.fileType);
        const isContact = CommonUtil.isVCFfile(message.attachment.fileType);
        if (isImage) {
          msgHeight = 200 + 6; // Added + 6 becuase image inside anchor tag 206px height;
        } else if (isContact) {
          msgHeight = msgHeight + 88; // Added 88 because contact card height is 88px;
        } else {
          const isAudio = CommonUtil.isAudio(message.attachment.fileType);
          if (isAudio) {
            msgHeight = 80;
          } else {
            const isSupportedVideo = CommonUtil.isSupportedVideo(message.attachment.fileType);
            if (isSupportedVideo) {
              msgHeight = 120;
            } else {
              msgHeight = 60; //file
            }
          }
        }
      // text
      } else if (isBodyHaveImage) {
        msgHeight = 200 + 6; // 200 + 6 is 200 is for image or video default size and 6 is for margin inside anchor tag.
      } else {
        const isMyMessage = this._isMyMessage(userJID, message.fromJid);

        let maxWidthFinal = clientWidth;

        // MOBILE
        if (CommonUtil.isMobileSize()) {

          if (isMyMessage) {
            maxWidthFinal -= 10; // .vnctalk-message.me {padding-right: 10px;}
            if (isGroupChat) {
              maxWidthFinal -= 44; // .vnctalk-message .message-content-container .message-content.me {margin-left: 44px;}
              maxWidthFinal -= 6;  // @media screen and (max-width: 767px).vnctalk-message .message-content-container .message-content.me {margin-right: 6px;}
            } else {
              maxWidthFinal -= 36; // @media screen and (max-width: 768px).vnctalk-message .message-content-container .message-content.me.is-single-chat {margin-left: 36px;}
            }
            maxWidthFinal -= (1 * 2);  // .vnctalk-message .message-content-container .message-content.me { border: solid 1px #dadee2; }
            maxWidthFinal -= (7 * 2); //@media screen and (max-width: 767px).vnctalk-message .margin0 {padding: 7px;}
          } else {
            maxWidthFinal -= 10; // .vnctalk-message { padding-right: 10px; }
            if (isGroupChat) {
              maxWidthFinal -= 32; // avatar
              maxWidthFinal -= 10; // @media screen and (max-width: 767px).vnctalk-message .my-avatar.groupchat { margin-left: 10px; }
              maxWidthFinal -= 10; // .vnctalk-message .message-content-container .message-content { margin-left: 10px; }
              maxWidthFinal -= 24; // .vnctalk-message .message-content-container .message-content:not(.me) {margin-right: 24px;}
            } else {
              maxWidthFinal -= (26 + 10); // @media screen and (max-width: 767px).vnctalk-message .message-content-container .message-content.is-single-chat:not(.me) {margin-right: 26px;margin-left: 10px;}
            }
            maxWidthFinal -= (1 * 2);  // .vnctalk-message .message-content-container .message-content.me { border: solid 1px #dadee2; }
            maxWidthFinal -= (7 * 2); //@media screen and (max-width: 767px).vnctalk-message .margin0 {padding: 7px;}
          }

        // DESKTOP
        } else {
          maxWidthFinal = Math.ceil((maxWidthFinal * 80) / 100); // vp-chat-window .chat-window-wrapper .chat-window .chat-list > cdk-virtual-scroll-viewport > div .vnctalk-message { max-width: 80%; }

          if (isMyMessage) {
            maxWidthFinal -= 12; // .vnctalk-message.me { padding-right: 12px; }
            if (isGroupChat) {
              maxWidthFinal -= 44; // .vnctalk-message .message-content-container .message-content.me { margin-left: 44px; }
            } else {
              maxWidthFinal -= 24; // .vnctalk-message .message-content-container .message-content.me.is-single-chat { margin-left: 24px; }
            }
            maxWidthFinal -= 2;  // .vnctalk-message .message-content-container .message-content.me {border: solid 1px #dadee2;}
            maxWidthFinal -= (23 + 8); // .vnctalk-message .margin0 { padding-right: 23px; padding-left: 8px;}
          } else {
            maxWidthFinal -= 10; // .vnctalk-message {padding-right: 10px;}
            maxWidthFinal -= 24; // .vnctalk-message .message-content-container .message-content:not(.me) { margin-right: 24px;}
            if (isGroupChat) {
              if (message.isDifferentSenderThanLastMessage) {
                maxWidthFinal -= 42; // avatar
              } else {
                maxWidthFinal -= 44; // element.style {padding-left: 44px;}
              }
            }
            maxWidthFinal -= 10; // .vnctalk-message .message-content-container .message-content { margin-left: 10px;}
            maxWidthFinal -= 2;  // .vnctalk-message .message-content-container .message-content { border: solid 1px #dadee2; }
            maxWidthFinal -= (23 + 8); // .vnctalk-message .margin0 { padding-right: 23px; padding-left: 8px;}
          }
        }
        testHeight = this.measureText(message.cachedContent, maxWidthFinal, fontSize, lineHeight, isHinTheme, message);
        msgHeight = testHeight;
        if (message.reactions) {
          msgHeight += VS_REACTION_HEIGHT;
        }
        if (!message.originalMessage) {
          const [fullUrl, urlsCount] = this.extractUrlFromMessageBodyAndCountUrls(message.cachedContent, jitsiURL);
          if (fullUrl && !message.forwardMessage) {
            // console.log("measureMessageTextHeight forward fullUrl", testHeight, msgHeight);
            const channelEntityHeight = this.getChannelEntityHeight(fullUrl);
            if (channelEntityHeight > 0) {
              msgHeight += channelEntityHeight;
            } else {
              const { isRedmineUrl, isVersionUrl } = this.getLinkType(fullUrl, urlsCount, redmineUrl);
              const shouldUnfurlURL = this.shouldUnfurlURL(isRedmineUrl ? fullUrl.split("\n")[0] : fullUrl);
              if (shouldUnfurlURL) {
                isUrlOrRedminePreview = true;
                if (isRedmineUrl) {
                  msgHeight += (CommonUtil.isMobileSize() ? VS_REDMINE_PREVIEW_HEIGHT + 90 : VS_REDMINE_PREVIEW_HEIGHT);
                } else if (isVersionUrl) {
                  msgHeight += VS_REDMINE_VERSION_PREVIEW_HEIGHT;
                } else {
                  msgHeight += VS_LINK_PREVIEW_HEIGHT;
                }
              }
            }
          }
        } else if (!!message.originalMessage?.replyMessage) {
          //////////////////
          //
          // COMMENTED originalHeight and reply Height BECAUSE ITS NOT WORKING PROPERTY and Trying to implement default message height.
          //
          ////////////////////

          // const originalHeight = this.measureText( CommonUtil.linkify( CommonUtil.processSpecialChars( CommonUtil.decodeHTML(message.originalMessage.body))), maxWidthFinal + 5, fontSize, lineHeight, isHinTheme, message);
          // const replyHeight = this.measureText(message.originalMessage.replyMessage, maxWidthFinal, fontSize, lineHeight, isHinTheme, message);
          // msgHeight = originalHeight + replyHeight + 20;
          msgHeight = msgHeight - 5; // Added -5 because it getting perfect with -5px.
          const [fullUrl, urlsCount] = this.extractUrlFromMessageBodyAndCountUrls(!!message.originalMessage?.replyMessage ? message.originalMessage.replyMessage : message.body, jitsiURL);
          if (fullUrl) {
            const channelEntityHeight = this.getChannelEntityHeight(fullUrl);
            if (channelEntityHeight > 0) {
              msgHeight += channelEntityHeight;
            } else {
              const { isRedmineUrl, isVersionUrl } = this.getLinkType(fullUrl, urlsCount, redmineUrl);
              const shouldUnfurlURL = this.shouldUnfurlURL(isRedmineUrl ? fullUrl.split("\n")[0] : fullUrl);
              if (shouldUnfurlURL) {
                isUrlOrRedminePreview = true;
                if (isRedmineUrl) {
                  msgHeight += (CommonUtil.isMobileSize() ? VS_REDMINE_PREVIEW_HEIGHT : VS_REDMINE_PREVIEW_HEIGHT);
                } else if (isVersionUrl) {
                  msgHeight += VS_REDMINE_VERSION_PREVIEW_HEIGHT;
                } else {
                  msgHeight += VS_LINK_PREVIEW_HEIGHT;
                }
              }
            }
          }

          // added below method to check if message.originalMessage.replyMessage have image or video.
          if (message && message.originalMessage && message.originalMessage.attachment) {
            const isOriginalMessageIsImageOrVideo = this.findAttachmentIsImageOrVideo(message.originalMessage.attachment);
            if (isOriginalMessageIsImageOrVideo) {
              msgHeight = msgHeight + 64 - 17 ; // Added 64 because image inside reply its height is 64px; // -17 because it getting perfect with -17px.
            }
          }

        }

      }

      // reply
      if (message.originalMessage) {
        msgHeight += VS_ORIGINAL_MESSAGE_ADDITION;
      }

      if (this._isMyMessage(userJID, message.fromJid)) {
        msgHeight = msgHeight + 4;  // Changed 3 to 4 for padding top and bottom 2px.
        // if (message.id.indexOf("isz8forooj") > -1) {
        //  console.log("CustomVirtualScrollStrategy debug isz8forooj isMyMessage ", msgHeight); //355
        // }
      } else {
        msgHeight = msgHeight + 4;  // Added + 4 because with other user it perfect with 4 extra.
      }

      // console.log("measureMessageTextHeight before and after", message.id, testHeight, msgHeight);
      // forward
      // <div class="forwared-message">
      if (message.forwardMessage) {
        // height +
        // .vnctalk-message .message-content-container .message-content .forwared-message {margin-bottom: 10px;}
        msgHeight += (17 + 10 + 6);
      }

      // <div class="vnctalk-message">
      // msgHeight = msgHeight + 2 + 2; // .vnctalk-message {padding-top: 2px; padding-bottom: 2px;}
      // if (CommonUtil.isMobileSize()) {
      //   msgHeight = msgHeight + 2; // @media screen and (max-width: 767px).vnctalk-message {margin-top: 2px;}
      // }

      // <div class="message-content">
      // msgHeight = msgHeight + 1 + 1; // .vnctalk-message .message-content-container .message-content {border: solid 1px #dadee2; }
      // msgHeight = msgHeight + 2; // .vnctalk-message .message-content-container .message-content {    margin-bottom: 5px;}

      // // <div class="margin0">
      // msgHeight = msgHeight + 7 + 7; // @media screen and (max-width: 767px).vnctalk-message .margin0 {padding: 7px;} OR  .vnctalk-message .margin0 { padding: 7px 23px 7px 8px; }
      // mesasge text
      // ...
      msgHeight = msgHeight + 3;  //.message-content it should 5 but for now I added 3

      if (CommonUtil.isMobileSize()) {
        msgHeight = msgHeight + 14; //.margin0 Added 14 because in mobile inside top and bottom padding is 7px each.
      } else {
        msgHeight = msgHeight + 24; //.margin0 Added 24 because in mobile inside top and bottom padding is 12px each.
      }
      if (isUrlOrRedminePreview) {
        msgHeight = msgHeight + 2; //. vnctalk-message .message-content-container .message-content .url-preview-metadata { border-top: 1px solid #dadada; }
        msgHeight = msgHeight + 5 ; // .vnctalk-message .message-content-container .message-content .message-time-status.web-preview-metadata {padding: 5px 0;}
      }

      // <div class="message-time-status">
      if (window.innerWidth > 1165) {
        msgHeight = msgHeight + 24 + 4; //  24 + .vnctalk-message .message-content-container .message-content .message-time-status {margin-top: 4px; }
      }
      else msgHeight = msgHeight + 18 + 4; //  18 + .vnctalk-message .message-content-container .message-content .message-time-status {margin-top: 4px; }
    }
    msgHeight = msgHeight + 4; // padding top and bottom added 4 because now padding top and bottom is 2px each.
    // <h6 class="conversation-date">
    message.isDifferentDayThanLastMessage = this.isDifferentDayThanLastMessage(idx, messages);
    if (message.isDifferentDayThanLastMessage) {
      msgHeight += 72; // I have changed this (15 + 5 + 14) to 72;
    }
    this.cacheMessageTextHeight(message.id, msgHeight, message.isDifferentSenderThanLastMessage, message.isDifferentDayThanLastMessage);

    return msgHeight;
  }

  static _isMyMessage(userJID: any, fromJid: string) {
    const isM = userJID && fromJid === userJID.bare;
    return isM;
  }

  static isEmojiOnly(str) {
    const emojiRegex = /^[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F700}-\u{1F77F}\u{1F780}-\u{1F7FF}\u{1F800}-\u{1F8FF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{2300}-\u{23FF}\u{2B50}\u{2B55}\u{3030}\u{303D}\u{3297}\u{3299}\u{1F004}\u{1F0CF}\u{1F1E6}-\u{1F1FF}\u{1F004}\u{1F18E}\u{1F201}\u{1F21A}\u{1F22F}\u{1F250}\u{1F251}\u{25AA}\u{25AB}\u{25FE}\u{25FD}\u{25FB}\u{25FC}\u{25B6}\u{25C0}\u{27A1}\u{2B05}\u{2B06}\u{2B07}\u{2194}\u{2195}\u{2196}\u{2197}\u{2198}\u{2199}\s\u{FE0F}\u{200D}\u{2640}\u{2642}\u{2695}\u{2696}\u{2697}\u{2699}\u{26A0}\u{26A1}\u{26AA}\u{26AB}\u{26B0}\u{26B1}\u{26C4}\u{26C5}\u{26C8}\u{26CE}\u{26CF}\u{26D1}\u{26D3}\u{26D4}\u{26E9}\u{26EA}\u{26F0}\u{26F1}\u{26F2}\u{26F3}\u{26F4}\u{26F5}\u{26F7}\u{26F8}\u{26F9}\u{26FA}\u{26FD}]+$/u;
    return emojiRegex.test(str);
  }

  static measureText(text: string, maxWidth: number, fontSize: number, lineHeight: number, isHinTheme: boolean, message) {
    const fontFamily = isHinTheme ? "Lato" : "Source Sans Pro";
    if (!!text && /^:[a-zA-Z0-9-_+]+:$/g.test(text) || this.isEmojiOnly(text)) { // Big emoji or only Emoji
      fontSize = 48;
    }
    // text.replace(/<p/g, "<p style='margin: 0;font-family: 'Source Sans Pro';font-size: 16px;'");
    // text.replace(/<h1/g, "<h1 style='font-size: 24px;margin: 5px 0;font-weight: bold;'");
    // text.replace(/<h2/g, "<h2 style='font-size: 20px;margin: 5px 0;font-weight: bold;'");
    // text.replace(/<h3/g, "<h3 style='font-size: 18px;margin: 5px 0;font-weight: bold;'");
    // text.replace(/<h4/g, "<h4 style='font-size: 16px;margin: 5px 0;font-weight: bold;'");
    // text.replace(/<h5/g, "<h5 style='font-size: 16px;margin: 5px 0;font-weight: bold;'");
    // text.replace(/<h6/g, "<h6 style='font-size: 16px;margin: 5px 0;font-weight: bold;'");
    let mainStyle = `width:${maxWidth + 50}px; word-wrap: break-word; white-space: pre-line; font-size: ${fontSize}px; font-family: "${fontFamily}"; line-height: ${lineHeight}`;
     if (CommonUtil.isMobileSize()) {
       mainStyle = `width:auto; word-wrap: break-word; white-space: pre-line; font-size: ${fontSize}px; font-family: "${fontFamily}"; line-height: ${lineHeight}`;
    }
    const div = document.createElement("div");
    const messageContentContainer = document.createElement("div");
    const messageContent = document.createElement("div");
    const messageText = document.createElement("div");
    div.className = "vnctalk-message";
    messageContentContainer.className = "message-content-container";
    messageContent.className = "message-content";
    if (message.type !== "groupchat") {
      messageContent.className = messageContent.className + " is-single-chat";
      div.className = div.className + " private-chat";
    }
    if (localStorage.getItem("userJID") === message.fromJid) {
      div.className = div.className + " me";
      messageContent.className = messageContent.className + " me";
    }
    // eslint-disable-next-line no-console
    // console.log("[measureText]", message);
    messageText.className = "text font-" + fontSize;
    messageText.style.paddingLeft = "18px";
    messageText.style.paddingRight = "18px";
    if (this.hasTwoColons(message.body)) {
      messageText.className = "emoji-only text";
    }
    messageText.innerHTML = text;
    messageContent.style.marginBottom = "0";
    messageContent.appendChild(messageText);
    messageContentContainer.appendChild(messageContent);
    div.appendChild(messageContentContainer);
    div.style.cssText = mainStyle;
    document.body.appendChild(div);
    // let testHeight = div.offsetHeight;
    // if (this.hasTwoColons(message.body)) {
      let testHeight = messageText.offsetHeight;
      // if (localStorage.getItem("userJID") !== message.fromJid) {
      //   testHeight = div.offsetHeight;
      // }

      if (message.cannotDecrypt || testHeight === 0) {
        testHeight = 20;
      }
    // }
    // if (CommonUtil.isMobileSize()) {
    //   testHeight += 10;
    // }
    // const totalLineBreaks = text.match(/<br>/g) !== null ? text.match(/<br>/g).length : 0;
    // testHeight = testHeight + totalLineBreaks * 6;
    div.remove();
    // eslint-disable-next-line no-console
    // console.log("measureText testHeight", text, testHeight);
    return testHeight;
  }

  static hasTwoColons(str: string): boolean {
      const body = str.trim(); // Remove leading and trailing spaces
      const colonsCount = (body.match(/:/g) || []).length; // Count colons in the string
      return colonsCount === 2 && body.startsWith(":") && body.endsWith(":");
  }

  static measureCallMessage(message, clientWidth, isHinTheme: boolean, userJID: any, repos: any) {
    const fontFamily = isHinTheme ? "Lato" : "Source Sans Pro";
    let mainStyle = `width:${400 + 50}px; word-wrap: break-word; white-space: pre-line; font-size: ${12}px; font-family: "${fontFamily}"; line-height: 1.67`;
  //   if (CommonUtil.isMobileSize()) {
  //     mainStyle = `width:auto; word-wrap: break-word; white-space: pre-line; font-size: ${fontSize}px; font-family: "${fontFamily}"; line-height: ${lineHeight}`;
  //  }

    const div = document.createElement("div");
    const titleDiv = document.createElement("div");
    const callHistoryP = document.createElement("p");

    div.className = "call-status";
    titleDiv.style.cssText = mainStyle;
    callHistoryP.className = "call-history";
    titleDiv.innerHTML = "started a call";
    callHistoryP.innerHTML = this.processMessage(message, userJID, repos);
    div.appendChild(titleDiv);
    div.appendChild(callHistoryP);
    div.style.cssText = mainStyle;
    document.body.appendChild(div);
    let divHeight = document.getElementsByClassName("call-status")[0].clientHeight + 2; // Added 2 because border is 1px.
    if (callHistoryP.innerHTML === "") {
      divHeight = 55; // Added 55 because if not call history text then it should be 55px.
    }
    div.remove();
    return divHeight;
  }


  // Added processMessage method to get message call history data like message.component.ts.
  static processMessage(message, userJID, repos) {
   return this.setCallHistory(message, userJID, repos);
  }

  // setCallHistory method is used to get message call history data like message.component.ts.
  static setCallHistory(message, userJID, repos: any) {
    if (message) {
      const callHistory = repos.conferenceRepo.callHistory[message.id];
      return this.renderCallHistory(callHistory, userJID, repos);
    }
  }


  // renderCallHistory method is used to get message call history data.
  static renderCallHistory(callHistoryMessages, userJID:any, repos: any): string {
    if (!callHistoryMessages) {
      return;
    }
    let content = [];
    let callDuration;
    let isAtLeastOneJoinSignal = false;
    let joinCountPerUser = {};
    repos.translate.get(["YOU", "USER_HAS_NOT_ANSWERED", "YOU_CALL", "YOU_JOINED", "YOU_REJOINED", "VERB", "STARTED", "JOINED", "REJOINED", "REJECTED", "ENDED", "CALL_ENDED"]).pipe(take(1)).subscribe(text => {
      callHistoryMessages.forEach(m => {
        let joinText = text.VERB;
        // name
        let name;
        if (userJID && m.fromJid === userJID?.bare) {
          name = text.YOU_CALL;
          joinText = "";
        } else {
          let contact: any;
          repos.contactRepo.getContactById(m.fromJid).pipe(take(1)).subscribe(c => {
            contact = c;
          });
          //
          if (!!contact) {
            name = contact.name;
          } else {
            name = !!m.fromJid ? m.fromJid.split("@")[0] : "";
          }
        }

        // action
        const eventType = m.vncTalkConference.eventType;
        let action = "";
        switch (eventType) {
          case "join": {

            // track re-joins?
            if (!joinCountPerUser[m.fromJid]) {
              joinCountPerUser[m.fromJid] = 0;
            }
            ++joinCountPerUser[m.fromJid];

            if (userJID && m.fromJid === userJID?.bare) {
              name = text.YOU;

              if (joinCountPerUser[m.fromJid] === 1) {
                action = text.YOU_JOINED;
              } else {
                action = text.YOU_REJOINED;
              }
            } else {
              if (joinCountPerUser[m.fromJid] === 1) {
                action = text.JOINED;
              } else {
                action = text.REJOINED;
              }
            }

            isAtLeastOneJoinSignal = true;

            break;
          }
          case "leave": action = joinText + " " + text.ENDED; break;
          case "cancel": action = joinText + " " + text.ENDED; break;
          case "reject": action = joinText + " " + text.REJECTED; break;
        }

        // call duration in a call is ended for you
        if (eventType === "leave" || eventType === "cancel") {
          if (userJID && m.fromJid === userJID?.bare) {
            if (m.vncTalkConference.duration) {
              callDuration = `. ${text.CALL_ENDED} - ${this.showDuration(m.vncTalkConference.duration)}`;
            }
          }
        } else if (eventType === "no-answer") {
          if (userJID && m.vncTalkConference.from === userJID?.bare) {
            this.noAnswer = text.USER_HAS_NOT_ANSWERED;
            this.callEnded = text.CALL_ENDED;
          }
        }

        // final content
        content.push(`${name} ${action.trim()}`);
      });
    });

    let res = content.join(", ");
    if (callDuration && isAtLeastOneJoinSignal) {
      res += callDuration;
    }

    return res;

  }

  static showDuration(time: string): string {
    if (time.startsWith("00:")) {
      time = time.slice(3, time.length);
    }
    return time;
  }


// Find image or video as attachment

  static findAttachmentIsImageOrVideo(dataAttachment, string: boolean = false) {
    let isImage = false;
    // Parse the attachment JSON string to an object
    let attachment: any = {};
    if (!string) {
      attachment = JSON.parse(dataAttachment);
    } else {
      attachment["url"] = dataAttachment;
    }

    // Check if the attachment has a URL with an image extension (e.g., .png, .jpg, .jpeg, .gif, etc.)
    if (attachment.url && /\.(png|jpg|jpeg|gif|mp4|mkv|avi|mov|flv|webm|3gp|ogg|mpg|mpeg)$/i.test(attachment.url)) {
      const spaceRegex = /\s/;
      isImage = !spaceRegex.test(attachment.url);
    } else {
      isImage = false;
    }

    return isImage;
  }


  //
  // cache

  static cacheMessageTextHeight(mid: string, testHeight: number, isDifferentSenderThanLastMessage: boolean, isDifferentDayThanLastMessage: boolean) {
    this.CATCH_MESSAGES_SIZE[mid] = {
      testHeight,
      isDifferentSenderThanLastMessage,
      isDifferentDayThanLastMessage
    };
  }

  static getMessageTextHeightFromCache(mid: string): any {
    const cacheMsg = this.CATCH_MESSAGES_SIZE[mid];
    return cacheMsg || null;
  }

  static deleteMessageTextHeightFromCache(mid: string) {
    delete this.CATCH_MESSAGES_SIZE[mid];
  }

  static timerChangeWindowSize: any;
  static resetCacheOnWindowSizeChange() {
    if (this.timerChangeWindowSize) {
      clearInterval(this.timerChangeWindowSize);
    }
    this.timerChangeWindowSize = setTimeout(() => {
      this.CATCH_MESSAGES_SIZE = {};


      this.timerChangeWindowSize = null;
    }, 1000);
  }

  //
  //

  static getChannelEntityHeight(firstUrl: string) {
    let res = 0;

    if (firstUrl.includes(ChannelEntityType.COMMENT) && firstUrl.includes(ChannelEntityType.TOPIC) && firstUrl.includes(ChannelEntityType.CHANNEL)) {
      // comment
      res = VS_CHANNEL_COMMENT;
    } else if (firstUrl.includes(ChannelEntityType.TOPIC) && firstUrl.includes(ChannelEntityType.CHANNEL)) {
      // topic
      res = VS_CHANNEL_TOPIC;
    } else if (firstUrl.includes(ChannelEntityType.SOCIAL)) {
      // social post
      res = VS_CHANNEL_SOCIAL;
    } else if (firstUrl.includes(ChannelEntityType.CHANNEL)) {
      if (firstUrl.includes(ChannelEntityType.SOCIAL_TOPIC)) {
        if (firstUrl.includes("/socialTopic/")) {
          // topic
          res = VS_CHANNEL_TOPIC;
        } else {
          // channel
          res = VS_CHANNEL_CHANNEL;
        }
      } else {
        //channel
        res = VS_CHANNEL_CHANNEL;
      }
    }
    return res;
  }

  static isHighlighted(msgIndex: number, msg: Message, unreadids: string[]) {
    const res = typeof msgIndex !== "undefined" ? (unreadids.length === 1 ? unreadids.includes(msg.id) : msgIndex < unreadids.length) : false;
    return res;
  }

  static isDifferentSenderThanLastMessage(currentMessageIndex: number, messages: Message[]) {

    const currentMessage = messages[currentMessageIndex];
    const messageAbove = messages[currentMessageIndex + 1];

    // TODO:
    // but, a 'messageAbove' can be from a diff user after got more, so what to do?
    // how to remove this flag form a prev message?
    if (currentMessageIndex === messages.length - 1 || !messageAbove) {
      return true;
    }

    if (!currentMessage) {
      return false;
    }

    const messageAboveIsCallInfo = messageAbove && (messageAbove.vncTalkConference || messageAbove.group_action);
    const currentMessageIsCallInfo = currentMessage && (currentMessage.vncTalkConference || currentMessage.group_action);

    const currentJid = currentMessage && currentMessage.fromJid;
    const jidAbove = messageAbove && messageAbove.fromJid;

    const res = (messageAboveIsCallInfo && !currentMessageIsCallInfo)
            || currentJid !== jidAbove
            || (!currentMessage.isDeleted && messageAbove.isDeleted);


    return res;
  }

  static isDifferentDayThanLastMessage(currentMessageIndex: number, messages: Message[]) {
    let res: boolean;

    // last
    if (currentMessageIndex === messages.length - 1) {
      res = false;
    } else {
      if (!messages[currentMessageIndex] || !messages[currentMessageIndex + 1]) {
        res = false;
      } else {
        res = !DateUtil.isSameDay(messages[currentMessageIndex + 1].timestamp, messages[currentMessageIndex].timestamp);
      }
    }


    return res;
  }

  static extractUrlFromMessageBodyAndCountUrls(body: string, jitsiURL: string) {
    let matches: any;
    let fullUrl: string;
    try {
      matches = body.match(/\bhttps?:\/\/\S+/gi); // complete body is url, followed by spaces maybe
      if (matches) {
        fullUrl = matches[0];
        if (!fullUrl.startsWith("http")) {
          fullUrl = `https://${fullUrl}`;
        }

        // skip call invites
        if (fullUrl?.includes("vnctalk-jitsi-meet") || fullUrl?.includes(jitsiURL)) {
          fullUrl = null;
        }
      }
    } catch (ex) {

    }


    return [fullUrl, matches?.length || 0];
  }

  static shouldUnfurlURL(url: string): boolean {
    let should = true;
    if (!environment.enablePreviewURL) {
      should = false;
    } else {
      const splitPath = url.split("/").filter((val) => { return val.length > 0; });
      if (splitPath.length > 2) {
        const fileName = url.split("/").pop().split("?")[0];
        const extension = fileName.toLowerCase().split(".").pop();
        if (fileName && fileName.split(".").length > 1 && !ALLOWED_PREVIEW_EXT.includes(extension)) {
          should = false;
        }
      }
    }


    return should;
  }

  static getLinkType(fullUrl: string, urlsCount: number, redmineUrl: string) {
    const res = {
      isImage: false,
      isAudio: false,
      isSupportedVideo: false,
      fileExtension: null,
      isRedmineUrl: false,
      isUrlPreview: false,
      isVersionUrl: false,
    };

    // if send multiple links in a single message - e.g. one with video and another one just a link, then we need to reset it all and set isUrlPreview=true only
    if (urlsCount === 1) {
      let fullUrlLowercase = fullUrl.toLowerCase();
      if (res.fileExtension = CommonUtil.imagesExtensions.find(ext => fullUrlLowercase.endsWith("." + ext))) {
        res.isImage = true;
      } else if (res.fileExtension = CommonUtil.audioExtensions.find(ext => fullUrlLowercase.endsWith("." + ext))) {
        res.isAudio = true;
      } else if (res.fileExtension = CommonUtil.supportedVideoExtensions.find(ext => fullUrlLowercase.endsWith("." + ext))) {
        res.isSupportedVideo = true;
      }
    }

    res.isRedmineUrl = this.checkIsRedmineUrl(fullUrl, redmineUrl);
    res.isVersionUrl = this.checkIsRedmineVersionUrl(fullUrl, redmineUrl);

    res.isUrlPreview = !res.isImage && !res.isAudio && !res.isSupportedVideo && !res.isRedmineUrl;


    return res;
  }

  static checkIsRedmineUrl(url: string, redmineUrl: string): boolean {
    return url.startsWith(redmineUrl + "/issues/") && !url.endsWith("/edit");
  }

  static checkIsRedmineVersionUrl(url: string, redmineUrl: string): boolean {
    return url.startsWith(redmineUrl + "/versions/") && !url.endsWith("/edit");
  }

  static isReceived(message: Message, loggedInJid: JID) {
    return message.from.bare === loggedInJid.bare;
  }

  static hasBody(message: Message) {
    return (message.body && message.body.trim()) || message.attachment;
  }

  static isValidMessage(message: Message): boolean {
    if (message.attachment) {
      return true;
    }

    if (message.type && message.type === "normal" && !message.vncTalkBroadcast) {
      return false;
    }

    if (message.vncTalkConference || message.notification) {
      return false;
    }

    if (message.vncTalkMuc) {
      return false;
    }

    if (message.startFile) {
      return false;
    }

    if (message.body && message.body.trim()) {
      return true;
    }

    return false;
  }

  static isInviteToCall(message: Message): boolean {
    if (message.vncTalkConference && message.vncTalkConference.eventType === "invite") {
      return true;
    }

    return false;
  }

  static isValidMessageFromServerHistory(message: Message): boolean {
    if (message.type && message.type === "normal") {
      return false;
    }


    if (message.vncTalkMuc || message.notification) {
      return false;
    }

    if (message.startFile) {
      return false;
    }

    if (message.body && message.body.trim()) {
      return true;
    }

    if (message.attachment) {
      return true;
    }
    return false;
  }

  static isFavouriteMessage(message: SearchMessage): boolean {
    if (!message.tags) {
      return false;
    }

    return message.tags.indexOf("*") !== -1;
  }

  static getTimeAgo(message: Message | SearchMessage) {
    return formatDistance(new Date(message.timestamp), new Date(), { addSuffix: true });
  }


  static convertMessageToFromStringToObject(to, resource?: string) {
    if (typeof to === "object") {
      return to;
    }

    to = to || "";
    return {
      local: to.split("@")[0],
      domain: to.split("@")[1],
      resource: resource ? resource : "some_random_res",
      bare: to,
      full: to,
      unescapedBare: to,
      unescapedFull: to,
      unescapedLocal: to.split("@")[0]
    };
  }

  static convertFromObject(from, room = undefined) {
    const randomResource = "some_random_res";

    if (room) {
      return {
        local: room.split("@")[0],
        domain: room.split("@")[1],
        full: room + "/" + from,
        resource: from,
        bare: room,
      };
    } else {
      return {
        local: from.split("@")[0],
        domain: from.split("@")[1],
        full: from + "/" + randomResource,
        resource: randomResource,
        bare: from,
      };
    }
  }

  static convertToXmppMessage(message: SearchMessage): Message {

    let htmlBody = message.htmlbody;
    const text = /<body xmlns=\"http:\/\/www.w3.org\/1999\/xhtml\">(.*)<\/body>/ig.exec(htmlBody);
    if (!!text && text[1]) {
      htmlBody = text[1];
    }
    let mStatus = MessageStatus.SENT;
    if (!!message.receipts) {
      mStatus = MessageStatus.DELIVERED;
    }

    message.id = message.id.replace(/.*_/ig, "");

    const response = {
      fromJid: message.from,
      body: message.body,
      htmlBody: htmlBody,
      timestamp: message.timestamp,
      file: null,
      isForwarded: false,
      isStarred: message.isStarred,
      status: mStatus,
      requestReceipt: true,
      id: message.id,
      tags: message.tags,
      to: this.convertMessageToFromStringToObject(message.to),
    };


    if (!!message.isStarred) {
      let tags = ["*"];
      if (!!response.tags && (response.tags.length > 0)) {
        response.tags.push("*");
      } else {
        response.tags = tags;
      }
      response["isStarred"] = true;
    }


    if (message.x_attachment) {
      response["attachment"] = JSON.parse(message.x_attachment);

      // check if this is an attach reply
      if (response["attachment"]["$t"]) {
        response["attachment"] = JSON.parse(response["attachment"]["$t"]);
      }
    }

    if (message.broadcast) {
      response["broadcast"] = message.broadcast;
    }
    if (message.expiry) {
      response["expiry"] = +message.expiry;
    }

    if (message.mention && message.mention !== "[]") {
      const mention = JSON.parse(message.mention);
      response["references"] = mention.map(m => {
        m = m.replace(/xmpp:+/ig, "");
        const data = {
          type: "mention",
          uri: `xmpp:${m}`
        };
        return data;
      });
    }

    if (message.sort_id) {
      response["sort_id"] = message.sort_id;
    }

    if (message.x_origMessage) {
      response["originalMessage"] = typeof message.x_origMessage === "string" ? JSON.parse(message.x_origMessage) : message.x_origMessage;
    }

    if (message.x_forwardMessage) {
      response["forwardMessage"] = typeof message.x_forwardMessage === "string" ? JSON.parse(message.x_forwardMessage) : message.x_forwardMessage;
      response["isForwarded"] = true;
    }

    if (message.x_vncConference) {
      response["vncTalkConference"] = JSON.parse(message.x_vncConference);
    }

    if (message.x_location) {
      response["location"] = JSON.parse(message.x_location);
    }

    if (message.x_replaceMsgId) {
      response["replace"] = { id: message.x_replaceMsgId };
      // response["id"] =  message.x_replaceMsgId;
      // response["isDeleted"] =  true;
      // response["body"] =  null;
    }

    if (message.room) {
      response["from"] = this.convertFromObject(message.from, message.room);
      response["type"] = "groupchat";
    } else {
      response["from"] = this.convertFromObject(message.from);
      response["type"] = "chat";
    }

    if (message.group_action){
      response["group_action"] = {type: message.group_action};
    }

    // omemo
    if (message.encrypted) {
      response["encrypted"] = message.encrypted;
    }
    if (message.encryption) {
      response["encryption"] = message.encryption;
    }

    if (!!message.reactions) {
      try {
        response["reactions"] = JSON.parse(message.reactions);
      } catch (error) {
         // eslint-disable-next-line no-console
        console.error("unable to parse message ", message);
      }
    }

    if (!!message.conversationTarget) {
      response["conversationTarget"] = message.conversationTarget;
      response["convTarget"] = message.conversationTarget;
    }

    return response;
  }

  static downloadFileFromUrl(url: string, conversationRepository?: ConversationRepository, filesStorageService?: FilesStorageService, store?: Store<TalkRootState>, messageDetails?: any) {
    if (environment.isCordova) {
      let target = "_system";
      if (typeof device !== "undefined"
        && device.platform
        && device.platform.toUpperCase() === "ANDROID"
        && (url.includes(".jpeg") || url.includes(".png") || url.includes(".jpg") || url.includes(".gif"))) {
        target = "_blank";
      }
      cordova.InAppBrowser.open(url, target, "location=no");
    } else if (environment.isElectron) {
      ElectronService.downloadFile(url);
    } else {
      if (!conversationRepository){
        if (url.indexOf("?") === -1) {
          window.open(url + "?d=true", "_blank");
        } else {
          window.open(url, "_blank");
        }
      } else {
        conversationRepository.downloadFile(messageDetails);
      }
    }
  }

  static isSystemMessage(message: Message) {
    return (!message.body || !message.body.trim()
      || message.chatState || (message.type !== "chat" && message.type !== "broadcast" && message.type !== "groupchat"));
  }

  static isActionMessage(message: Message) {
    return !!message.group_action || !!message.vncTalkConference;
  }
}
